import { gql, useMutation, useQuery } from '@apollo/client';
import DocumentViewer from '@orn/shared-web/components/DocumentViewer';
import formatCalendarDate from '@orn/shared-web/utils/formatCalendarDate';
import formatDateTime from '@orn/shared-web/utils/formatDateTime';
import * as React from 'react';

import AdminSelectUser, { type IAdminSelectUserSelection } from '../../components/admin/AdminSelectUser';
import Button from '../../components/Button';
import Header from '../../components/Header';
import { useToast } from '../../components/Toast';

import CreateBillModal from './CreateBillModal';
import CreateBillSubmissionModal from './CreateBillSubmissionModal';

import type {
  AdminDeleteBillMutation,
  AdminDeleteBillMutationVariables,
  AdminDeleteBillSubmissionMutation,
  AdminDeleteBillSubmissionMutationVariables,
  AdminListBillsQuery,
  AdminListBillsQueryVariables,
  AdminListBillSubmissionsQuery,
  AdminListBillSubmissionsQueryVariables,
} from './__generated__/BillManagementScene.gql';

const ADMIN_LIST_BILLS = gql`
  query AdminListBills($familyId: ID!) {
    adminListBills(familyId: $familyId) {
      id
      billSubmission {
        id
      }
      createdAt
      updatedAt
      providerName
      amountDue
      dueDate
      status
      source
      description
      accountNumber
      invoiceNumber
      minAmountDue
      statementDate
      serviceStartDate
      serviceEndDate
      isAutopay
      paymentUrl
      lateFeeAmount
      lateFeeDate
      providerPhone
      providerEmail
      payerName
      previousAccountBalance
      documents {
        id
        name
        mimeType
        sequenceNumber
        assetUrl
        previewUrl
        emailMetadata {
          originalFrom
          originalTo
          originalSubject
        }
      }
    }
  }
`;

const ADMIN_LIST_BILL_SUBMISSIONS = gql`
  query AdminListBillSubmissions($familyId: ID!) {
    adminListBillSubmissions(familyId: $familyId) {
      id
      createdAt
      updatedAt
      providerName
      amountDue
      dueDate
      status
      source
      description
      accountNumber
      invoiceNumber
      minAmountDue
      statementDate
      serviceStartDate
      serviceEndDate
      isAutopay
      paymentUrl
      lateFeeAmount
      lateFeeDate
      providerPhone
      providerEmail
      payerName
      previousAccountBalance
      documents {
        id
        name
        mimeType
        sequenceNumber
        assetUrl
        previewUrl
        emailMetadata {
          originalFrom
          originalTo
          originalSubject
        }
      }
    }
  }
`;

const ADMIN_DELETE_BILL = gql`
  mutation AdminDeleteBill($billId: ID!) {
    adminDeleteBill(billId: $billId)
  }
`;

const ADMIN_DELETE_BILL_SUBMISSION = gql`
  mutation AdminDeleteBillSubmission($billSubmissionId: ID!) {
    adminDeleteBillSubmission(billSubmissionId: $billSubmissionId)
  }
`;

/**
 * Bill management admin tool. Operators can browse bills and bill submissions
 * for a selected user, view attached documents, soft-delete records, and
 * create new bills / submissions via modal forms. Migrated from the consumer
 * `@orn/web` admin pane — same GraphQL contract, different transport
 * (mops-api) and operator-tuned styling.
 */
const BillManagementScene = () => {
  const [isCreateBillModalOpen, setIsCreateBillModalOpen] = React.useState(false);
  const [isCreateBillSubmissionModalOpen, setIsCreateBillSubmissionModalOpen] = React.useState(false);
  const [selection, setSelection] = React.useState<IAdminSelectUserSelection | null>(null);
  const [expandedBillIds, setExpandedBillIds] = React.useState<Record<string, boolean>>({});
  const [expandedBillSubmissionIds, setExpandedBillSubmissionIds] = React.useState<Record<string, boolean>>({});
  const [viewerDocsBillId, setViewerDocsBillId] = React.useState<string | null>(null);
  const [viewerDocsSubmissionId, setViewerDocsSubmissionId] = React.useState<string | null>(null);

  const familyId = selection?.familyId ?? null;

  const {
    data: billsData,
    loading: billsLoading,
    refetch: refetchBills,
  } = useQuery<AdminListBillsQuery, AdminListBillsQueryVariables>(ADMIN_LIST_BILLS, {
    variables: { familyId: familyId ?? '' },
    skip: !familyId,
  });

  const {
    data: billSubmissionsData,
    loading: billSubmissionsLoading,
    refetch: refetchBillSubmissions,
  } = useQuery<AdminListBillSubmissionsQuery, AdminListBillSubmissionsQueryVariables>(ADMIN_LIST_BILL_SUBMISSIONS, {
    variables: { familyId: familyId ?? '' },
    skip: !familyId,
  });

  const [deleteBill] = useMutation<AdminDeleteBillMutation, AdminDeleteBillMutationVariables>(ADMIN_DELETE_BILL);
  const [deleteBillSubmission] = useMutation<
    AdminDeleteBillSubmissionMutation,
    AdminDeleteBillSubmissionMutationVariables
  >(ADMIN_DELETE_BILL_SUBMISSION);
  const { showToast } = useToast();

  const handleDeleteBill = React.useCallback(
    async (billId: string) => {
      if (!familyId) {
        return;
      }
      if (
        !window.confirm('Are you sure you want to delete this bill? This will also unlink any associated transactions.')
      ) {
        return;
      }

      try {
        await deleteBill({ variables: { billId } });
        await Promise.all([refetchBills(), refetchBillSubmissions()]);
        showToast({ type: 'success', message: 'Bill deleted.' });
      } catch {
        showToast({ type: 'error', message: 'Failed to delete bill. Please try again.' });
      }
    },
    [familyId, deleteBill, refetchBills, refetchBillSubmissions, showToast]
  );

  const handleDeleteBillSubmission = React.useCallback(
    async (billSubmissionId: string) => {
      if (!familyId) {
        return;
      }
      if (
        !window.confirm(
          'Are you sure you want to delete this bill submission? This will also delete any associated bill and unlink transactions.'
        )
      ) {
        return;
      }

      try {
        await deleteBillSubmission({ variables: { billSubmissionId } });
        await Promise.all([refetchBills(), refetchBillSubmissions()]);
        showToast({ type: 'success', message: 'Bill submission deleted.' });
      } catch {
        showToast({ type: 'error', message: 'Failed to delete bill submission. Please try again.' });
      }
    },
    [familyId, deleteBillSubmission, refetchBills, refetchBillSubmissions, showToast]
  );

  const sortedBills = React.useMemo(() => {
    if (!billsData?.adminListBills) {
      return [];
    }
    return [...billsData.adminListBills].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [billsData?.adminListBills]);

  const sortedBillSubmissions = React.useMemo(() => {
    if (!billSubmissionsData?.adminListBillSubmissions) {
      return [];
    }
    return [...billSubmissionsData.adminListBillSubmissions].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [billSubmissionsData?.adminListBillSubmissions]);

  const formatFieldValue = (value: unknown): string => {
    if (value == null || value === '') {
      return 'No value';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'object' && 'year' in value && 'month' in value && 'day' in value) {
      return formatCalendarDate(value as Parameters<typeof formatCalendarDate>[0], 'long');
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'bigint') {
      return `${value}`;
    }
    return 'No value';
  };

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-6 px-6 py-5">
        <div className="flex flex-col gap-1">
          <h1 className="text-sm font-semibold uppercase tracking-widest">Bill management</h1>
          <p className="text-fg-secondary text-xs">Create bill records and bill submission records for users.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button color="primary" size="sm" onClick={() => setIsCreateBillModalOpen(true)}>
            Create bill
          </Button>
          <Button color="secondary" size="sm" onClick={() => setIsCreateBillSubmissionModalOpen(true)}>
            Create bill submission
          </Button>
        </div>

        <div className="w-full max-w-md">
          <AdminSelectUser selectedUserId={selection?.userId ?? null} onSelectionChange={setSelection} />
        </div>

        {!familyId ? (
          <div className="text-fg-tertiary text-xs italic">Select a user to view bills and submissions.</div>
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="border-border-primary bg-bg-primary border p-4">
              <div className="mb-3 text-sm font-semibold uppercase tracking-wider">Bills</div>
              {billsLoading ? (
                <div className="text-fg-secondary text-xs">Loading bills…</div>
              ) : sortedBills.length ? (
                <div className="flex flex-col gap-3">
                  {sortedBills.map((bill) => {
                    const isExpanded = Boolean(expandedBillIds[bill.id]);
                    return (
                      <div key={bill.id} className="bg-bg-secondary border-border-primary border p-3">
                        <div className="text-sm font-semibold">
                          {bill.providerName || 'No provider'} · {bill.status}
                        </div>
                        <div className="text-fg-tertiary font-mono text-xs">ID: {bill.id}</div>
                        <div className="text-fg-tertiary font-mono text-xs">
                          Bill submission ID: {bill.billSubmission?.id || 'No bill submission'}
                        </div>
                        <div className="text-fg-tertiary text-xs">Amount: {bill.amountDue || 'No amount'}</div>
                        <div className="text-fg-tertiary text-xs">
                          Due date: {formatCalendarDate(bill.dueDate, 'long')}
                        </div>
                        <div className="text-fg-tertiary text-xs">Source: {bill.source}</div>
                        <div className="text-fg-tertiary text-xs">Created at: {formatDateTime(bill.createdAt)}</div>
                        {isExpanded && (
                          <>
                            <div className="text-fg-tertiary text-xs">Updated at: {formatDateTime(bill.updatedAt)}</div>
                            <div className="text-fg-tertiary text-xs">
                              Description: {formatFieldValue(bill.description)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Account number: {formatFieldValue(bill.accountNumber)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Invoice number: {formatFieldValue(bill.invoiceNumber)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Min amount due: {formatFieldValue(bill.minAmountDue)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Statement date: {formatFieldValue(bill.statementDate)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Service start date: {formatFieldValue(bill.serviceStartDate)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Service end date: {formatFieldValue(bill.serviceEndDate)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Is autopay: {formatFieldValue(bill.isAutopay)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Payment URL: {formatFieldValue(bill.paymentUrl)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Late fee amount: {formatFieldValue(bill.lateFeeAmount)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Late fee date: {formatFieldValue(bill.lateFeeDate)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Provider phone: {formatFieldValue(bill.providerPhone)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Provider email: {formatFieldValue(bill.providerEmail)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Payer name: {formatFieldValue(bill.payerName)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Previous account balance: {formatFieldValue(bill.previousAccountBalance)}
                            </div>
                          </>
                        )}
                        {!isExpanded && bill.description && (
                          <div className="text-fg-secondary mt-1 text-sm">{bill.description}</div>
                        )}
                        <div className="mt-2 flex gap-3">
                          <button
                            type="button"
                            className="text-fg-brand-primary hover:text-fg-brand-secondary text-xs font-medium"
                            onClick={() => setExpandedBillIds((prev) => ({ ...prev, [bill.id]: !prev[bill.id] }))}
                          >
                            {isExpanded ? 'Show less' : 'Show all fields'}
                          </button>
                          {bill.documents.length > 0 && (
                            <button
                              type="button"
                              className="text-fg-brand-primary hover:text-fg-brand-secondary text-xs font-medium"
                              onClick={() => setViewerDocsBillId(bill.id)}
                            >
                              View attachments ({bill.documents.length})
                            </button>
                          )}
                          <button
                            type="button"
                            className="text-fg-error-primary text-xs font-medium"
                            onClick={() => void handleDeleteBill(bill.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-fg-tertiary text-xs italic">No bills found.</div>
              )}
            </div>

            <div className="border-border-primary bg-bg-primary border p-4">
              <div className="mb-3 text-sm font-semibold uppercase tracking-wider">Bill submissions</div>
              {billSubmissionsLoading ? (
                <div className="text-fg-secondary text-xs">Loading bill submissions…</div>
              ) : sortedBillSubmissions.length ? (
                <div className="flex flex-col gap-3">
                  {sortedBillSubmissions.map((billSubmission) => {
                    const isExpanded = Boolean(expandedBillSubmissionIds[billSubmission.id]);
                    return (
                      <div key={billSubmission.id} className="bg-bg-secondary border-border-primary border p-3">
                        <div className="text-sm font-semibold">
                          {billSubmission.providerName || 'No provider'} · {billSubmission.status}
                        </div>
                        <div className="text-fg-tertiary font-mono text-xs">ID: {billSubmission.id}</div>
                        <div className="text-fg-tertiary text-xs">
                          Amount: {billSubmission.amountDue || 'No amount'}
                        </div>
                        <div className="text-fg-tertiary text-xs">
                          Due date:{' '}
                          {billSubmission.dueDate ? formatCalendarDate(billSubmission.dueDate, 'long') : 'No due date'}
                        </div>
                        <div className="text-fg-tertiary text-xs">Source: {billSubmission.source}</div>
                        <div className="text-fg-tertiary text-xs">
                          Created at: {formatDateTime(billSubmission.createdAt)}
                        </div>
                        {isExpanded && (
                          <>
                            <div className="text-fg-tertiary text-xs">
                              Updated at: {formatDateTime(billSubmission.updatedAt)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Description: {formatFieldValue(billSubmission.description)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Account number: {formatFieldValue(billSubmission.accountNumber)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Invoice number: {formatFieldValue(billSubmission.invoiceNumber)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Min amount due: {formatFieldValue(billSubmission.minAmountDue)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Statement date: {formatFieldValue(billSubmission.statementDate)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Service start date: {formatFieldValue(billSubmission.serviceStartDate)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Service end date: {formatFieldValue(billSubmission.serviceEndDate)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Is autopay: {formatFieldValue(billSubmission.isAutopay)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Payment URL: {formatFieldValue(billSubmission.paymentUrl)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Late fee amount: {formatFieldValue(billSubmission.lateFeeAmount)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Late fee date: {formatFieldValue(billSubmission.lateFeeDate)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Provider phone: {formatFieldValue(billSubmission.providerPhone)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Provider email: {formatFieldValue(billSubmission.providerEmail)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Payer name: {formatFieldValue(billSubmission.payerName)}
                            </div>
                            <div className="text-fg-tertiary text-xs">
                              Previous account balance: {formatFieldValue(billSubmission.previousAccountBalance)}
                            </div>
                          </>
                        )}
                        {!isExpanded && billSubmission.description && (
                          <div className="text-fg-secondary mt-1 text-sm">{billSubmission.description}</div>
                        )}
                        <div className="mt-2 flex gap-3">
                          <button
                            type="button"
                            className="text-fg-brand-primary hover:text-fg-brand-secondary text-xs font-medium"
                            onClick={() =>
                              setExpandedBillSubmissionIds((prev) => ({
                                ...prev,
                                [billSubmission.id]: !prev[billSubmission.id],
                              }))
                            }
                          >
                            {isExpanded ? 'Show less' : 'Show all fields'}
                          </button>
                          {billSubmission.documents.length > 0 && (
                            <button
                              type="button"
                              className="text-fg-brand-primary hover:text-fg-brand-secondary text-xs font-medium"
                              onClick={() => setViewerDocsSubmissionId(billSubmission.id)}
                            >
                              View attachments ({billSubmission.documents.length})
                            </button>
                          )}
                          <button
                            type="button"
                            className="text-fg-error-primary text-xs font-medium"
                            onClick={() => void handleDeleteBillSubmission(billSubmission.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-fg-tertiary text-xs italic">No bill submissions found.</div>
              )}
            </div>
          </div>
        )}

        <CreateBillModal isOpen={isCreateBillModalOpen} onOpenChange={setIsCreateBillModalOpen} />
        <CreateBillSubmissionModal
          isOpen={isCreateBillSubmissionModalOpen}
          onOpenChange={setIsCreateBillSubmissionModalOpen}
        />

        <DocumentViewer
          documents={sortedBills.find((b) => b.id === viewerDocsBillId)?.documents ?? []}
          isOpen={viewerDocsBillId !== null}
          onClose={() => setViewerDocsBillId(null)}
        />
        <DocumentViewer
          documents={sortedBillSubmissions.find((s) => s.id === viewerDocsSubmissionId)?.documents ?? []}
          isOpen={viewerDocsSubmissionId !== null}
          onClose={() => setViewerDocsSubmissionId(null)}
        />
      </main>
    </div>
  );
};

export default BillManagementScene;
